﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The default AccessDenied page. This page handles AccessDenied through form username/password, loading of credentials
// from existing authentication token and loading of credentials from server side session (used by most
// SSO solutions).

// Can be tested for all customers. It's the default page when not yet authenticated.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import classnames from "classnames";

import { useIntl, IntlContext } from "~base/Internationalization/IntlContext";
import { useSettings } from "~base/Settings/SettingsContext";
import { useAuthToken } from "~base/Services/API/AuthToken";
import { Button } from "~base/Components/Atoms/Button";
import { RouteLink } from "~base/Components/Primitives/Link";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import PoweredByConsilio from "~base/Components/Composite/PoweredByConsilio";
import {
    AccessDeniedPageCustomizations,
    AccessDeniedPageDefaultCustomization,
} from "./Customization";
import { SplashPage } from "~base/Pages/Shared/SplashPage";
import { useAdditionalStyles } from "./Styles";
import { SplashContent } from "~base/Pages/Shared/SplashContent";
import {
    getSameSiteSafeUrlParam,
    useNavigateService,
} from "~base/Services/NavigateService";
import TextBlock from "~base/Components/Primitives/TextBlock";
import UserInfoCard from "~base/Pages/Shared/UserInfoCard";
import { useServerContext } from "~base/Services/ServerContext";
import { isDenialReasonTextCode } from "~base/Services/DenialReason";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type AccessDeniedProps = {
    customizations?: AccessDeniedPageCustomizations;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const AccessDeniedPage: React.FunctionComponent<AccessDeniedProps> = (
    props: AccessDeniedProps
) => {
    const customization = {
        ...AccessDeniedPageDefaultCustomization,
        ...props.customizations,
    };

    const intl = useIntl();
    const navigate = useNavigateService();
    const [searchParams] = useSearchParams();
    const settings = useSettings();

    const hideLogout = settings.valueAsBool("Account.HideLogout");
    const extLogoutUrl = settings.valueAsString("account.externalLogoutUrl");
    const logoutUrl =
        customization?.logoutButtonUrl ?? extLogoutUrl ?? "/Account/Logout";
    const logoutButtonTextCode =
        customization?.logoutButtonTextCode ?? "VisiFinance.UI.Main.LoggaUt";

    const userService = useServerContext();
    const user = userService.user;

    const name =
        user?.Name ??
        user?.UserName ??
        intl.formatText("VisiFinance.UI.Start.Obehörig");
    const role = user?.Roles?.find((x) => true)?.Name;
    const roleTextCode = role ? resolveRoleName(intl, role) : null;

    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const paramReturnUrl = getSameSiteSafeUrlParam(searchParams, "ReturnUrl");

    const jssSpacingStyles = useJssSpacingStyles();
    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const [_, token] = useAuthToken({});

    useEffect(() => {
        if (token == null && paramReturnUrl) {
            redirectToLogin(serverVirtualPath, paramReturnUrl);
        }
    }, [token, serverVirtualPath, paramReturnUrl]);

    const classes = useAdditionalStyles(customization)();

    const headerContent = (
        <UserInfoCard
            className={classes.header}
            displayName={name}
            role={roleTextCode}
            customization={customization.userInfoCustomizations}
        />
    );

    const footerContent =
        hideLogout != true ? (
            <RouteLink testIdentifier="AccessDeniedLogoutLink" to={logoutUrl}>
                <Button
                    testIdentifier="AccessDeniedLogoutButton"
                    className={classnames(formVerticalSpacingClasses)}
                >
                    {intl.formatText(logoutButtonTextCode)}
                </Button>
            </RouteLink>
        ) : null;

    const isSessionlessFeature = isSessionlessFeatureUrl(paramReturnUrl);

    // The server states a reason when it refuses a session, e.g. that it is too old to resume. Prefer it
    // over the generic message, but only when it is a denial reason the tenant has a text for.
    const paramFaultCode = searchParams.get("faultCode");
    const faultTextCode =
        isDenialReasonTextCode(paramFaultCode) &&
        intl.tryFormatText(paramFaultCode)
            ? paramFaultCode
            : null;

    const errorTextCode =
        faultTextCode ??
        customization.errorMessageTextCode ??
        "VisiFinance.UI.AccessDenied.DefaultMessage";

    return (
        <SplashPage isLoading={false} showSplashScreen={false}>
            <SplashContent header={headerContent} footer={footerContent}>
                <TextBlock
                    size={"Small"}
                    styleType={"Paragraph"}
                    text={getErrorMessage(intl, role, errorTextCode)}
                />
                {isSessionlessFeature && (
                    <TextBlock
                        styleType={"Paragraph"}
                        size={"Small"}
                        textCode={
                            "VisiFinance.UI.AccessDenied.SessionlessFeature"
                        }
                    />
                )}
            </SplashContent>
            <div className={classes.bottom}>
                <PoweredByConsilio testIdentifier="PoweredByConsilio" />
                {customization.adminMenu && customization.adminMenu(role)}
            </div>
        </SplashPage>
    );
};

//-------------------------------------------------------------------------------------
// Functions
//-------------------------------------------------------------------------------------

const getErrorMessage = (
    intl: IntlContext,
    role: string,
    errorTextCode: string
) => {
    const roleSpecificMessage = intl.tryFormatText(`${errorTextCode}.${role}`);
    if (roleSpecificMessage) {
        return roleSpecificMessage;
    }
    return intl.formatText(errorTextCode);
};

const isSessionlessFeatureUrl = (url: string) => {
    if (!url) return false;

    if (url.toLowerCase().indexOf("/swagger") != -1) {
        return true;
    }

    if (url.toLowerCase().indexOf("/hangfire") != -1) {
        return true;
    }

    return false;
};

const redirectToLogin = (serverVirtualPath: string, returnUrl: string) => {
    const redirectUrl = `${serverVirtualPath}Account/Login?ReturnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = redirectUrl;
};

const resolveRoleName = (intl: IntlContext, role: string) => {
    switch (role) {
        case "customeranonymous":
            return "VisiFinance.UI.Start.Roll.AnonymKund";
        case "customer":
            return "VisiFinance.UI.Start.Roll.Kund";
        case "advisor":
            return "VisiFinance.UI.Start.Roll.Rådgivare";
        case "support":
            return "VisiFinance.UI.Start.Roll.Support";
        case "sysadmin":
            return "VisiFinance.UI.Start.Roll.Systemadministratör";
        case "tenantadmin":
            return "VisiFinance.UI.Start.Roll.Tenantadministratör";
        case "developer":
            return "VisiFinance.UI.Start.Roll.Utvecklare";
        case "assistant":
            return "VisiFinance.UI.Start.Roll.Assistent";
        default:
            return null;
    }
};
